// Parser + helpers voor de nieuwe partnervergoeding-bestanden
// (partner_commission_export / partner_commission_Credit_export).
//
// Deze bestanden vervangen facturen.csv / creditfacturen.csv ALLEEN voor de
// Revenue-pagina. Ze hebben 46 kolommen met andere namen/volgorde dan de oude
// facturen-export, maar dezelfde betekenis. We mappen op KOLOMNAAM (niet op
// vaste index) zodat een herordening van Odoo de parser niet breekt, en geven
// objecten terug met exact de veldnamen die de Revenue-berekening al gebruikt.
//
// Belangrijke eigenschappen van de data (geverifieerd op echte export):
//   - Eén factuur kan over MEERDERE records verdeeld zijn (per GSRN-lijn).
//   - kWh/bedrag-velden (Invoiced KWH/Amount ...) zijn per regel → per record
//     sommeren is correct (zoals de fee-berekening al doet).
//   - 'Totaal excl. BTW' / 'Totaal' zijn per record HERHAALD en per factuur
//     constant → bij een omzet-sommatie per uniek factuurnummer tellen
//     (dedupeByInvoice). Het factuurnummer ('Nummer') is de unieke sleutel,
//     formaat bv. VKEVI/202604/03242 of VOHOA/202605/01084.
//   - Geen 'Relatie/Bedrijf' (energielabel) en geen 'Verkoopteam' kolom:
//       * energielabel leiden we af uit de 3e–5e letter van het factuurnummer.
//       * verkoopteam leiden we af uit de centrale sales-structuur (/resellers)
//         op basis van verkoper + energielabel.

// Energielabel-prefix (letters 3-5 van het factuurnummer) → energielabel-naam.
// Voorbeeld: VKEVI/... of VOEVI/... → 'EVI' → Evident Energie.
export const LABEL_PREFIX_MAP = {
  HOA: 'HOA Energy',
  EVI: 'Evident Energie',
  P2Y: 'Power2You',
  SMA: 'Smappee Smiles',
  SER: 'Servolt Energie',
};

// Kolomnaam (genormaliseerd) → intern veld. We normaliseren headers (lowercase,
// spaties/accenten gestript) zodat kleine variaties matchen.
const HEADER_FIELD_MAP = {
  'verkoper': 'verkoper',
  'relatie/referentie': 'relatieReferentie',
  'relatie/is een bedrijf': 'isCompany',
  'el order/contract/naam': 'elContract',
  'ng order/contract/naam': 'ngContract',
  'nummer': 'nummer',
  'factuurdatum': 'factuurdatum',
  'factuur relatie weergavenaam': 'relatieNaam',
  'soort': 'soort',
  'vervaldatum': 'vervaldatum',
  'status betaling': 'statusBetaling',
  'el order/opportunity stage': 'elOpportunityStage',
  'el order/delivery start date': 'elDeliveryStart',
  'el order/connection point gsrn': 'elGSRN',
  'el order/connection point status': 'elCPStatus',
  'el order/product groep/titel': 'elProductGroep',
  'el order/ratecard/naam': 'elRatecard',
  'el order/product groep/klanten segment': 'elSegment',
  'el order/price additions el': 'elPriceAdditions',
  'invoiced kwh el offtake': 'elKwhOfftake',
  'invoiced amount el offtake': 'elAmountOfftake',
  'el order/price formula coefficient el offtakenet': 'elCoeffOfftake',
  'el order/price formula type el offtakenet': 'elTypeOfftake',
  'el order/price formula majoration el offtakenet': 'elMajorationOfftake',
  'invoiced kwh el injection': 'elKwhInjection',
  'invoiced amount el injection': 'elAmountInjection',
  'el order/price formula coefficient el injection': 'elCoeffInjection',
  'el order/price formula type el injection': 'elTypeInjection',
  'el order/price formula majoration el injection': 'elMajorationInjection',
  'invoiced kwh imbalance': 'elKwhImbalance',
  'invoiced amount imbalance': 'elAmountImbalance',
  'ng order/opportunity stage': 'ngOpportunityStage',
  'ng order/delivery start date': 'ngDeliveryStart',
  'ng order/connection point gsrn': 'ngGSRN',
  'ng order/connection point status': 'ngCPStatus',
  'ng order/product groep/titel': 'ngProductGroep',
  'ng order/ratecard/naam': 'ngRatecard',
  'ng order/product groep/klanten segment': 'ngSegment',
  'ng order/price additions ng': 'ngPriceAdditions',
  'invoiced kwh ng offtake': 'ngKwhOfftake',
  'invoiced amount ng offtake': 'ngAmountOfftake',
  'ng order/price formula coefficient ng offtakenet': 'ngCoeffOfftake',
  'ng order/price formula type ng offtakenet': 'ngTypeOfftake',
  'ng order/price formula majoration ng offtakenet': 'ngMajorationOfftake',
  'totaal excl. btw': 'totaalExclBTW',
  'totaal': 'totaal',
};

// Velden die als getal geparset moeten worden (de rest blijft tekst).
const NUMERIC_FIELDS = new Set([
  'elPriceAdditions', 'elKwhOfftake', 'elAmountOfftake', 'elMajorationOfftake',
  'elKwhInjection', 'elAmountInjection', 'elMajorationInjection',
  'elKwhImbalance', 'elAmountImbalance',
  'ngPriceAdditions', 'ngKwhOfftake', 'ngAmountOfftake', 'ngMajorationOfftake',
  'totaalExclBTW', 'totaal',
]);

const normHeader = (h) => String(h || '')
  .replace(/﻿/g, '')
  .trim()
  .toLowerCase()
  .replace(/ë/g, 'e').replace(/é/g, 'e').replace(/è/g, 'e')
  .replace(/\s+/g, ' ');

const parseDecimal = (val) => {
  if (val == null || val === '' || val === '#N/B') return 0;
  return parseFloat(String(val).replace(/[€\s]/g, '').replace(',', '.')) || 0;
};

const normSegment = (raw) => {
  const s = String(raw || '').trim();
  const l = s.toLowerCase();
  return (l === 'aangepast' || l === 'aanegepast' || l === 'custom') ? 'CUSTOM' : s;
};

// Leid het energielabel af uit het factuurnummer (letters 3-5). Onbekend → ''.
export function deriveLabelFromNumber(nummer) {
  const code = String(nummer || '').replace(/^[^A-Za-z]*/, '').slice(2, 5).toUpperCase();
  return LABEL_PREFIX_MAP[code] || '';
}

// Parse een rijen-array (eerste rij = header) → records met interne veldnamen.
// `rows` is een array van arrays (uit xlsx.js readTabularFile of readCsvRows).
// Records zonder herkend energielabel krijgen relatieBedrijf='' en
// _unmappedLabel=true zodat de UI ze kan signaleren en verbergen.
export function parsePartnerCommission(rows, { isCredit = false } = {}) {
  if (!rows || rows.length < 2) return [];
  const header = rows[0].map(normHeader);
  // kolomindex → veld
  const colField = header.map(h => HEADER_FIELD_MAP[h] || null);

  const out = [];
  for (let i = 1; i < rows.length; i++) {
    const r = rows[i];
    if (!r || r.every(c => (c || '').trim() === '')) continue;
    const rec = { id: `${isCredit ? 'pcredit' : 'pcomm'}-${i}` };
    for (let c = 0; c < colField.length; c++) {
      const field = colField[c];
      if (!field) continue;
      const raw = r[c];
      rec[field] = NUMERIC_FIELDS.has(field) ? parseDecimal(raw) : (raw == null ? '' : String(raw).trim());
    }
    rec.elSegment = normSegment(rec.elSegment);
    rec.ngSegment = normSegment(rec.ngSegment);
    rec.relatieBedrijf = deriveLabelFromNumber(rec.nummer);
    rec._unmappedLabel = !rec.relatieBedrijf;
    rec.verkoopteam = ''; // wordt later verrijkt via sales-structuur
    // Velden die de oude facturen-shape had maar hier ontbreken → lege defaults
    // zodat downstream-code (calcEL e.d.) niet op undefined stuit.
    if (rec.elMeterMethod == null) rec.elMeterMethod = '';
    // Creditnota's: bedragen negatief verrekenen, net als de oude CSV-pipeline.
    if (isCredit) {
      rec.elAmountOfftake = (rec.elAmountOfftake || 0) * -1;
      rec.elAmountInjection = (rec.elAmountInjection || 0) * -1;
      rec.elAmountImbalance = (rec.elAmountImbalance || 0) * -1;
      rec.ngAmountOfftake = (rec.ngAmountOfftake || 0) * -1;
      rec.totaalExclBTW = (rec.totaalExclBTW || 0) * -1;
      rec.totaal = (rec.totaal || 0) * -1;
    }
    out.push(rec);
  }
  return out;
}

// Segment-normalisatie voor de API-variant: ET geeft 'b2c'/'b2b'/'custom'
// (lowercase). We uppercasen naar 'B2C'/'B2B' en mappen custom-varianten naar
// 'CUSTOM' — exact de waarden die RevenuePage verwacht (calc + display-badges).
const apiSegment = (s) => {
  const u = String(s || '').trim().toUpperCase();
  if (u === 'AANGEPAST' || u === 'AANEGEPAST' || u === 'CUSTOM') return 'CUSTOM';
  return u;
};

// Map één ET partner-commission API-item (uit /invoices/partner-commission of
// /selfbills/partner-commission, gefilterd op company_id) naar exact dezelfde
// interne record-shape die parsePartnerCommission produceert. Zo gebruiken de
// API- en bestand-werkwijze identiek dezelfde Revenue-berekening/verrijking.
//   - relatieBedrijf (energielabel) komt rechtstreeks uit it.label (company_id
//     filter geeft de echte labelnaam, bv. "Evident Energie").
//   - verkoopteam blijft leeg → wordt later via enrichCommissionTeams gezet.
//   - isCredit (type === 'out_refund') verrekent bedragen negatief.
// Strip de juridische-entiteit-suffix die partner-commission soms aan het label
// hangt: "HOA Energy (Energy Together B.V.)" → "HOA Energy". Eigen-entiteit-labels
// (bv. "Evident Energie") blijven ongewijzigd.
const cleanCommissionLabel = (l) => String(l || '').replace(/\s*\([^)]*\)\s*$/, '').trim();

// De ET-API gebruikt Engelse status/type-codes; de Revenue-berekening verwacht de
// Nederlandse waarden van de oude bestand-export (betaald/verkoopfactuur/…). Zonder
// deze normalisatie valt o.a. de Goed/Wanbetaler/Overig-split (facturenGoed) leeg en
// blijven alle afgeleide tabellen leeg.
const PAYMENT_STATUS_NL = { paid: 'Betaald', not_paid: 'Niet betaald', partial: 'Niet betaald', in_payment: 'Niet betaald', reversed: 'Omgekeerd' };
// out_refund = klant-creditnota (zelfde label als de bestand-import: "Klant
// creditfactuur") zodat credits in de detailtabel/filter/export consistent
// herkenbaar zijn. De teken-omkering draait op het `type`-veld (isCredit), niet op
// deze tekst — dit is puur weergave. in_refund = self-bill credit (apart).
const TYPE_NL = { out_invoice: 'Verkoopfactuur', in_invoice: 'Verkoopfactuur', out_refund: 'Klant creditfactuur', in_refund: 'Creditnota' };
const mapPaymentStatusNL = (s) => PAYMENT_STATUS_NL[String(s || '').toLowerCase().trim()] || 'Niet betaald';
const mapTypeNL = (t) => TYPE_NL[String(t || '').toLowerCase().trim()] || '';

export function mapApiCommissionItem(it, { isCredit = false, label = null } = {}) {
  const el = it.el || {};
  const ng = it.ng || {};
  const eo = el.offtake || {}, ei = el.injection || {}, eb = el.imbalance || {}, no = ng.offtake || {};
  const n = (v) => (v == null ? 0 : Number(v) || 0);
  const rec = {
    verkoper: it.salesperson || '',
    // De verkoper zoals hij op de factuur zelf staat, als die afwijkt van de
    // huidige verkoper van het order (de server zet `salesperson` op die van het
    // order — de factuur volgt het order, zie commissionSync). Leeg = gelijk.
    verkoperFactuur: it.salesperson_factuur && it.salesperson_factuur !== it.salesperson ? it.salesperson_factuur : '',
    relatieReferentie: (it.customer && it.customer.reference) || '',
    relatieNaam: (it.customer && it.customer.name) || '',
    isCompany: it.customer && it.customer.is_company ? 'True' : '',
    nummer: it.number || '',
    factuurdatum: it.invoice_date || '',
    vervaldatum: it.due_date || '',
    soort: mapTypeNL(it.type),
    statusBetaling: mapPaymentStatusNL(it.payment_status),
    elContract: it.contract || '',
    ngContract: it.contract || '',
    elOpportunityStage: el.opportunity_stage || '',
    elDeliveryStart: el.delivery_start || '',
    elGSRN: el.gsrn || '',
    elCPStatus: el.cp_status || '',
    elProductGroep: el.product_group || '',
    elRatecard: el.ratecard || '',
    elSegment: apiSegment(el.segment),
    elPriceAdditions: n(el.price_additions),
    elKwhOfftake: n(eo.kwh),
    elAmountOfftake: n(eo.amount),
    elCoeffOfftake: n(eo.coeff),
    elTypeOfftake: eo.type || '',
    elMajorationOfftake: n(eo.majoration),
    elKwhInjection: n(ei.kwh),
    elAmountInjection: n(ei.amount),
    elCoeffInjection: n(ei.coeff),
    elTypeInjection: ei.type || '',
    elMajorationInjection: n(ei.majoration),
    elKwhImbalance: n(eb.kwh),
    elAmountImbalance: n(eb.amount),
    ngOpportunityStage: ng.opportunity_stage || '',
    ngDeliveryStart: ng.delivery_start || '',
    ngGSRN: ng.gsrn || '',
    ngCPStatus: ng.cp_status || '',
    ngProductGroep: ng.product_group || '',
    ngRatecard: ng.ratecard || '',
    ngSegment: apiSegment(ng.segment),
    ngPriceAdditions: n(ng.price_additions),
    ngKwhOfftake: n(no.kwh),
    ngAmountOfftake: n(no.amount),
    ngCoeffOfftake: n(no.coeff),
    ngTypeOfftake: no.type || '',
    ngMajorationOfftake: n(no.majoration),
    totaalExclBTW: n(it.total_excl_btw),
    totaal: n(it.total),
    elMeterMethod: '',
    verkoopteam: '',
  };
  // Energielabel: gebruik bij voorkeur het opgevraagde label (de records zijn per
  // company_id/energielabel opgehaald → gegarandeerd consistent met de filter),
  // anders het API-label zonder juridische-entiteit-suffix.
  rec.relatieBedrijf = label || cleanCommissionLabel(it.label);
  rec._unmappedLabel = !rec.relatieBedrijf;
  if (isCredit) {
    rec.elAmountOfftake *= -1;
    rec.elAmountInjection *= -1;
    rec.elAmountImbalance *= -1;
    rec.ngAmountOfftake *= -1;
    rec.totaalExclBTW *= -1;
    rec.totaal *= -1;
  }
  return rec;
}

// Bouw verkoper→team lookup per energielabel uit de centrale sales-structuur
// (zelfde bron als alle filters). Geeft een functie (label, seller) → team|''.
export function makeTeamResolver(salesStructure) {
  const slt = (salesStructure && salesStructure.sellers_by_label_team) || {};
  const byLabel = new Map(); // label → Map(seller → team)
  for (const label of Object.keys(slt)) {
    const m = new Map();
    const teams = slt[label] || {};
    for (const team of Object.keys(teams)) {
      for (const seller of teams[team] || []) {
        if (!m.has(seller)) m.set(seller, team);
      }
    }
    byLabel.set(label, m);
  }
  return (label, seller) => {
    const m = byLabel.get(label);
    return (m && m.get(seller)) || '';
  };
}

// Verrijk records in-place met verkoopteam uit de sales-structuur.
// Placeholder-team voor verkopers zonder team-match → getoond als "Online".
const UNKNOWN_TEAM = 'Online';
export function enrichCommissionTeams(records, salesStructure) {
  const resolve = makeTeamResolver(salesStructure);
  for (const rec of records) {
    rec.verkoopteam = resolve(rec.relatieBedrijf, rec.verkoper) || UNKNOWN_TEAM;
  }
  return records;
}

// Dedupliceer bedrag-velden per uniek factuurnummer. Geeft de SOM van
// 'totaalExclBTW' en 'totaal' waarbij records met hetzelfde Nummer maar één keer
// meetellen (de waarde is per factuur constant). Records zonder nummer tellen
// elk als eigen "factuur". Optionele `filterFn` om vooraf te filteren.
export function sumByInvoice(records, filterFn = null) {
  const seen = new Map(); // nummer → { exclBTW, totaal }
  let i = 0;
  for (const rec of records) {
    if (filterFn && !filterFn(rec)) continue;
    const key = rec.nummer || `__norow_${i++}`;
    if (!seen.has(key)) seen.set(key, { exclBTW: rec.totaalExclBTW || 0, totaal: rec.totaal || 0 });
  }
  let exclBTW = 0, totaal = 0;
  for (const v of seen.values()) { exclBTW += v.exclBTW; totaal += v.totaal; }
  return { exclBTW, totaal, invoiceCount: seen.size };
}

// Tel unieke facturen in een record-set.
export function countInvoices(records) {
  const s = new Set();
  let i = 0;
  for (const rec of records) s.add(rec.nummer || `__norow_${i++}`);
  return s.size;
}
